import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  TableContext,
  TableData,
  TableFilterDataItem,
  TableHeaderCategoryConfig,
} from "./TableContext";
import {
  getTableDataWithSelection,
  toggleTableItemSelection,
  toggleTableSelection,
} from "../helpers/tableSelection";

type Props = {
  children: React.ReactNode;
  initialTableSearch?: object;
  initialTableFilter?: object;
  initialTablePagination?: { page: number; rowsPerPage: number };
};

const TableProvider = ({
  children,
  initialTableSearch,
  initialTableFilter,
  initialTablePagination,
}: Props) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [tableHeader, setTableHeader] = useState<TableHeaderCategoryConfig[]>(
    [],
  );
  const [tablePagination, setTablePagination] = useState({
    page: initialTablePagination?.page ?? 1,
    rowsPerPage: initialTablePagination?.rowsPerPage ?? 25,
  });
  const [tableSearch, setTableSearch] = useState<object | null>(
    initialTableSearch ?? null,
  );
  const [tableFilter, setTableFilter] = useState<object | null>(
    initialTableFilter ?? null,
  );
  const [tableOrder, setTableOrder] = useState<object | null>(null);
  const [tableGlobalSearch, setTableGlobalSearch] = useState("");
  const [tableGlobalFilter, setTableGlobalFilter] = useState("");
  const [globalFilterOptions, setGlobalFilterOptions] = useState<
    null | TableFilterDataItem[]
  >(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const [debouncedTableContextValues, setDebouncedTableContextValues] =
    useState<any>(null);

  // we debounce the table context values to avoid multiple requests
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTableContextValues({
        tableSearch,
        tableFilter,
        tablePagination,
      });
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [tablePagination, tableSearch, tableFilter]);

  const handleSetTablePagination = useCallback(
    (
      callBack: (prev: { page: number; rowsPerPage: number }) => {
        page: number;
        rowsPerPage: number;
      },
    ) => {
      setTablePagination(callBack);
    },
    [],
  );

  const deleteItem = useCallback((id: string | number) => {
    setTableData((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleTableFilter = useCallback(
    (
      key: string,
      value: string | number,
      options: { resetPage?: boolean } = {},
    ) => {
      if (
        typeof key === "string" &&
        (typeof value === "string" || typeof value === "number")
      ) {
        setTableFilter((prev: any) => {
          if (prev?.[key] === value) return prev;
          if (prev?.[key] === undefined && value === "") return prev;
          if (options.resetPage !== false) {
            setTablePagination((pagination) => ({ ...pagination, page: 1 }));
          }
          return { ...prev, [key]: value };
        });
      }
    },
    [],
  );

  const handleTableSearch = useCallback(
    (
      key: string,
      value: string | number,
      options: { resetPage?: boolean } = {},
    ) => {
      if (
        typeof key === "string" &&
        (typeof value === "string" || typeof value === "number")
      ) {
        setTableSearch((prev: any) => {
          if (prev?.[key] === value) return prev;
          if (prev?.[key] === undefined && value === "") return prev;
          if (options.resetPage !== false) {
            setTablePagination((pagination) => ({ ...pagination, page: 1 }));
          }
          return { ...prev, [key]: value };
        });
      }
    },
    [],
  );

  const handleTableOrder = useCallback((key: string) => {
    if (typeof key === "string") {
      // @ts-ignore
      setTableOrder((prev) => {
        // @ts-ignore
        if (prev && prev[key] === "asc") {
          return { [key]: "desc" };
        }
        return { [key]: "asc" };
      });
    }
  }, []);

  const handleCheckboxClick = useCallback(
    (
      crudData: any,
      checkboxId?: string | number,
      tableItemId?: string | number,
    ) => {
      const itemId = checkboxId ?? tableItemId ?? crudData?.id;
      if (itemId !== undefined && itemId !== null) {
        setSelectedItems((prev) =>
          toggleTableItemSelection(prev, itemId, crudData),
        );
      }
    },
    [],
  );

  const handleCheckAllItems = useCallback(() => {
    setSelectedItems((current) => toggleTableSelection(current, tableData));
  }, [tableData]);

  const tableDataWithCheckedItems = useMemo(
    () => getTableDataWithSelection(tableData, selectedItems),
    [tableData, selectedItems],
  );

  return (
    <TableContext.Provider
      value={{
        tableData: tableDataWithCheckedItems,
        setTableData,
        tableHeader,
        setTableHeader,
        tablePagination,
        handleSetTablePagination,
        tableSearch,
        setTableSearch,
        handleTableSearch,
        tableFilter,
        setTableFilter,
        handleTableFilter,
        tableOrder,
        handleTableOrder,
        deleteItem,
        tableGlobalSearch,
        setTableGlobalSearch,
        tableGlobalFilter,
        setTableGlobalFilter,
        globalFilterOptions,
        setGlobalFilterOptions,
        handleCheckboxClick,
        selectedItems,
        setSelectedItems,
        handleCheckAllItems,
        debouncedTableContextValues,
      }}>
      {children}
    </TableContext.Provider>
  );
};

export default TableProvider;
